import { trpc } from '@/lib/trpc';

/** YouTube watch/short URL → embeddable URL, or null. */
function youtubeEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.endsWith('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
      if (u.pathname.startsWith('/embed/')) return url;
    }
  } catch { /* not a URL */ }
  return null;
}

function vimeoEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.endsWith('vimeo.com')) {
      const id = u.pathname.replace(/\//g, '');
      if (/^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* not a URL */ }
  return null;
}

function isDirectVideo(url: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

/**
 * Free intro/preview video for a course. Plays for everyone (even guests):
 * the server's courses.getIntroStreamUrl is public and signs R2 keys or
 * passes through external links. Renders nothing if the course has no intro.
 */
export default function IntroVideo({ courseId }: { courseId: number }) {
  const { data, isLoading } = trpc.courses.getIntroStreamUrl.useQuery({ courseId });
  const url = data?.url ?? null;

  if (isLoading) {
    return (
      <div className="aspect-video bg-[#09090B] rounded-2xl border border-[#27272A]
                      flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!url) return null;

  const yt = youtubeEmbed(url);
  const vm = yt ? null : vimeoEmbed(url);

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="bg-yellow-400/10 text-yellow-400 text-[10px] font-bold uppercase
                         tracking-widest px-2 py-0.5 rounded-full border border-yellow-400/20">
          Free preview
        </span>
      </div>
      <div className="aspect-video bg-black rounded-2xl border border-[#27272A] overflow-hidden">
        {yt ? (
          <iframe src={yt} title="Course preview" className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen />
        ) : vm ? (
          <iframe src={vm} title="Course preview" className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
        ) : isDirectVideo(url) ? (
          <video src={url} controls className="w-full h-full" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-center p-4">
            <a href={url} target="_blank" rel="noopener noreferrer"
               className="mp-btn mp-btn-outline px-5 py-2 text-sm">Open preview ↗</a>
          </div>
        )}
      </div>
    </div>
  );
}
